import { useEffect, useMemo, useRef, useState } from 'react';

import { debounce, isEqual } from 'lodash';

import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { isTest } from 'utils/environment';

import { useGoogleAutocompleteRestrictions } from './use-google-autocomplete-restrictions';
import { useGoogleGeolocationLibrary } from './use-google-geolocation-library';
import { usePostalCodeListAddresses } from './use-postal-code-list-addresses';

const DEFAULT_DELAY_MS: number = isTest ? 0 : 300;
const LOCATION_LENGTH_THRESHOLD_FOR_PREDICTIONS: number = 3;

let autocompleteService: google.maps.places.AutocompleteService;

export interface IUseLocationPredictionsOptions {
  delayMs?: number;
  eventSource?: CustomEventNames;
  includeExactMatch?: boolean;
  matchTypes?: ('address' | '(regions)' | 'geocode' | 'establishment' | '(cities)')[];
  shouldExpandPostalCodeToAddresses?: boolean;
  skip?: boolean;
}

export function useLocationPredictions(
  location: string,
  {
    delayMs = DEFAULT_DELAY_MS,
    eventSource,
    includeExactMatch = false,
    matchTypes = [],
    shouldExpandPostalCodeToAddresses = false,
    skip,
  }: IUseLocationPredictionsOptions = {}
) {
  const cdp = useCdpContext();
  const { libraryLoaded } = useGoogleGeolocationLibrary();
  const getAddressList = usePostalCodeListAddresses();
  const componentRestrictions = useGoogleAutocompleteRestrictions();

  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);

  // Since the `matchTypes` prop is an array and `useEffect()` depends on it,
  // we keep it stable by using a ref
  const matchTypesRef = useRef(matchTypes);
  if (!isEqual(matchTypes, matchTypesRef.current)) {
    matchTypesRef.current = matchTypes;
  }

  if (libraryLoaded && !autocompleteService) {
    autocompleteService = new window.google.maps.places.AutocompleteService();
  }

  const getPlacePredictionsDebounced = useMemo(() => {
    const createRequest = (input: string): google.maps.places.AutocompletionRequest => ({
      componentRestrictions,
      input,
      types: matchTypesRef.current,
    });

    const handlePlacePredictions =
      (input: string) =>
      (
        results: google.maps.places.AutocompletePrediction[],
        status: google.maps.places.PlacesServiceStatus
      ) => {
        const hasResults =
          status === window.google.maps.places.PlacesServiceStatus.OK && results?.length;

        if (!hasResults) {
          cdp.trackEvent({
            name: CustomEventNames.MANUAL_LOCATION_SEARCH,
            type: EventTypes.Search,
            attributes: {
              searchInput: input,
              selectedAddress: '',
              Name: eventSource,
            },
          });
        }

        setPredictions(hasResults ? results : []);

        // if only one zip code listed in the predictions, search for the full
        // addresses in the zip code when indicated so by the
        // `shouldExpandPostalCodeToAddresses` option.
        const resultHasOnlyPostalCode =
          results?.length === 1 && results[0].types.includes('postal_code');
        if (shouldExpandPostalCodeToAddresses && resultHasOnlyPostalCode) {
          const postalCodePlaceId = results[0].place_id;

          getAddressList(input, postalCodePlaceId).then(addressList => {
            if (addressList) {
              setPredictions(results.concat(addressList));
            }
          });
        }
      };

    return debounce((input: string) => {
      autocompleteService?.getPlacePredictions(createRequest(input), handlePlacePredictions(input));
    }, delayMs);
  }, [
    cdp,
    componentRestrictions,
    delayMs,
    eventSource,
    getAddressList,
    shouldExpandPostalCodeToAddresses,
  ]);

  useEffect(() => {
    if (!libraryLoaded) {
      return;
    }

    if (!location || location.length < LOCATION_LENGTH_THRESHOLD_FOR_PREDICTIONS || skip) {
      getPlacePredictionsDebounced.cancel();
      setPredictions([]);
      return;
    }

    getPlacePredictionsDebounced(location);
  }, [getPlacePredictionsDebounced, libraryLoaded, location, skip]);

  if (!includeExactMatch) {
    return predictions.filter(prediction => prediction.description !== location);
  }

  return predictions;
}
